import api from './api';

/**
 * AI Document Generation API — the 3-step wizard (plan → OCR extract → generate).
 * The shared `api` instance unwraps to the response body ({ success, message, data })
 * and preserves `error.code` / `error.data` (e.g. INSUFFICIENT_CREDITS) for callers.
 */
const aiDocumentService = {
    // Template catalog + the credit cost the owner will be charged per document.
    getTemplates: (params = {}) => api.get('/api/ai-documents/templates', { params }),

    // Step 1 — plan (or re-plan an existing draft: pass { docId }).
    plan: (payload) => api.post('/api/ai-documents/plan', payload),

    // Step 1b — a form read from a sample photo: name its fillable blanks (free, text-only).
    planFields: (docId) => api.post('/api/ai-documents/plan-fields', { docId }, { timeout: 120000 }),

    // Step 2 — OCR one uploaded source image (base64 data URL). Vision can be slow.
    extract: (payload) => api.post('/api/ai-documents/extract', payload, { timeout: 120000 }),

    // Step 2 — AI auto-fill: generate professional values for the empty fields.
    suggest: (payload) => api.post('/api/ai-documents/suggest', payload, { timeout: 120000 }),

    // Step 2 (background) — get/create the cached {token} template while the user
    // scans. Free (no credits). `force: true` refreshes the cached template.
    template: (payload) => api.post('/api/ai-documents/template', payload, { timeout: 120000 }),

    // Step 3 — finalize: token-replace the cached template with the collected values
    // (no AI, instant). Deducts credits. `refreshTemplate: true` re-drafts first.
    finalize: (payload) => api.post('/api/ai-documents/finalize', payload, { timeout: 120000 }),

    // Step 3 — generate the document with AI (deducts credits). Kept for regenerate
    // on the document detail page.
    generate: (payload) => api.post('/api/ai-documents/generate', payload, { timeout: 120000 }),

    // List generated documents for the active office space.
    list: (params = {}) => api.get('/api/ai-documents', { params }),

    // One document (with plan / fields / blocks).
    get: (id) => api.get(`/api/ai-documents/${id}`),

    // Rename and/or persist inline block edits: { name?, blocks? }.
    update: (id, payload) => api.patch(`/api/ai-documents/${id}`, payload),

    // Re-run generation for an existing document (deducts credits again).
    regenerate: (id, payload = {}) => api.post(`/api/ai-documents/${id}/regenerate`, payload, { timeout: 120000 }),

    // Soft-delete (the token/cost record is retained server-side).
    remove: (id) => api.delete(`/api/ai-documents/${id}`),
};

export default aiDocumentService;
